import type { Dictionary } from '../i18n';

export type TourId = 'welcome' | 'review' | 'playbooks';

export interface TourStep {
  /** `data-tour` value to anchor on. Empty string => a centered modal step. */
  anchor: string;
  title: string;
  body: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
}

export interface TourDef {
  id: TourId;
  /** Decides whether this tour should auto-start on the current path. */
  matchRoute: (path: string) => boolean;
  steps: TourStep[];
}

const isChatRoute = (path: string) => path.startsWith('/qagent/');

/**
 * Build the tour definitions for the active locale. Tour copy comes from the
 * i18n dictionary so tours are bilingual. Anchors reference `data-tour`
 * attributes on the live UI; steps whose anchor is absent at runtime are
 * skipped by the driver wrapper (handles feature-flag-gated UI and timing).
 */
export function buildTours(t: Dictionary): Record<TourId, TourDef> {
  const w = t.onboarding.tours.welcome;
  const r = t.onboarding.tours.review;
  const p = t.onboarding.tours.playbooks;

  return {
    welcome: {
      id: 'welcome',
      matchRoute: (path) => path === '/',
      steps: [
        { anchor: 'sidebar-new-chat', title: w.newChat.title, body: w.newChat.body, side: 'right' },
        { anchor: 'sidebar-new-contract', title: w.newContract.title, body: w.newContract.body, side: 'right' },
        { anchor: 'sidebar-projects', title: w.projects.title, body: w.projects.body, side: 'right' },
        { anchor: 'sidebar-playbooks', title: w.playbooks.title, body: w.playbooks.body, side: 'right' },
        { anchor: 'home-input', title: w.messageBox.title, body: w.messageBox.body, side: 'top' },
        { anchor: 'home-prompts', title: w.prompts.title, body: w.prompts.body, side: 'top' },
        { anchor: 'sidebar-install-word', title: w.installWord.title, body: w.installWord.body, side: 'right' },
        { anchor: 'sidebar-user-menu', title: w.userMenu.title, body: w.userMenu.body, side: 'right' },
        { anchor: '', title: w.finish.title, body: w.finish.body },
      ],
    },
    review: {
      id: 'review',
      matchRoute: isChatRoute,
      steps: [
        { anchor: 'chat-input', title: r.mention.title, body: r.mention.body, side: 'top' },
        { anchor: 'chat-resources', title: r.resources.title, body: r.resources.body, side: 'bottom' },
        { anchor: 'suggestion-accept', title: r.suggestion.title, body: r.suggestion.body, side: 'left' },
        { anchor: 'suggestions-export', title: r.export.title, body: r.export.body, side: 'left' },
      ],
    },
    playbooks: {
      id: 'playbooks',
      matchRoute: (path) => path === '/playbooks',
      steps: [
        { anchor: 'playbooks-title', title: p.intro.title, body: p.intro.body, side: 'bottom' },
        { anchor: 'playbooks-create', title: p.create.title, body: p.create.body, side: 'bottom' },
        { anchor: 'playbooks-import', title: p.import.title, body: p.import.body, side: 'bottom' },
        { anchor: 'playbooks-create-ai', title: p.ai.title, body: p.ai.body, side: 'bottom' },
        { anchor: '', title: p.apply.title, body: p.apply.body },
      ],
    },
  };
}

/** Auto-start priority order when several tours could match a route. */
export const AUTO_START_ORDER: TourId[] = ['welcome', 'review', 'playbooks'];
